import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import BaseModal from '@uikit/Modals/BaseModal/BaseModal';
import Button from '@uikit/Form/Button/Button';
import Alert from '@uikit/Alert/Alert';
import ProgressBar from '@uikit/old/ProgressBar/ProgressBar';
import { SVGIcon } from '@uikit/old/Icon/Icon';

import { uploadFile } from '@api/fileImport.api';
import { addNotification } from '@redux/actions/notifications.actions';
import { getCurrentWorkspaceId } from '@redux/selectors/workspaces.selectors';
import { billingPublisherDetailsSelector } from '@redux/selectors/billings-publisher.selectors';
import { fetchPublisherBillingDetails } from '@redux/thunks/billings-publisher.thunks';
import { PublisherWebsiteImportHookType } from '@hooks/usePublisherWebsiteImport';
import { parseApiErrorMessage } from '@utils/errorUtils';
import { ApiError } from 'src/errors/ApiError';

import CsvDropZone from './_components/CsvDropZone/CsvDropZone';
import ImportStatusDetails from './_components/ImportStatusDetails/ImportStatusDetails';
import ImportColumnsReference from './_components/ImportColumnsReference/ImportColumnsReference';
import {
  BYTES_IN_MB,
  CSV_EXTENSION,
  IMPORT_MAX_FILE_SIZE_MB,
  ImportModalView,
  getImportMaxRows,
  UPLOAD_INTERRUPTED_MESSAGE,
  UPLOAD_RETRY_ATTEMPTS,
} from './constants';
import {
  countCsvDataRows,
  downloadImportReport,
  downloadImportTemplate,
  isTransportError,
} from './utils';

import './ImportWebsitesModal.scss';

type ImportWebsitesModalPropsType = {
  isOpen: boolean;
  onClose: () => void;
  websiteImport: PublisherWebsiteImportHookType;
};

function ImportWebsitesModal({
  isOpen,
  onClose,
  websiteImport,
}: ImportWebsitesModalPropsType): JSX.Element {
  const dispatch = useDispatch();
  const workspaceId = useSelector(getCurrentWorkspaceId);
  const { data: billingDetails } = useSelector(billingPublisherDetailsSelector);
  // RES-7310: one file can fill the whole account, so the cap follows the website limit the
  // admin sets per publisher. The server enforces the same number on launch.
  const maxRows = getImportMaxRows(billingDetails?.websiteLimit);
  const { item, isInProgress, isLaunching, isCanceling, launch, cancel, refetch } = websiteImport;

  const [view, setView] = useState<ImportModalView>('upload');
  const [error, setError] = useState<string>('');
  const [uploadingFileName, setUploadingFileName] = useState<string>('');
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [isTemplateLoading, setIsTemplateLoading] = useState<boolean>(false);
  const [isReportLoading, setIsReportLoading] = useState<boolean>(false);
  const [isReferenceOpen, setIsReferenceOpen] = useState<boolean>(false);

  // Every opening starts clean: a finished import is never shown again here (its summary and
  // report live in the finish notification), only a still-running one resumes its status view.
  useEffect(() => {
    if (isOpen) {
      setView(isInProgress ? 'status' : 'upload');
      setError('');
      setIsReferenceOpen(false);
      // Refresh the limit: an admin may have raised it since the billing details were loaded.
      fetchPublisherBillingDetails(true)(dispatch).catch(() => undefined);
    }
  }, [isOpen]);

  // The whole file travels in one grpc-web message; a dropped connection or a proxy reset
  // surfaces as a transport error, and the bytes are still in memory, so one silent retry
  // is cheap. Backend rejections (wrong format, size) are final and are not retried.
  const uploadWithRetry = async (
    file: File,
    buffer: ArrayBuffer,
    attempt = 0
  ): ReturnType<typeof uploadFile> => {
    try {
      return await uploadFile(undefined, workspaceId, file, buffer, ([{ progress }]) =>
        setUploadProgress(progress)
      );
    } catch (e) {
      if (isTransportError(e) && attempt < UPLOAD_RETRY_ATTEMPTS) {
        setUploadProgress(0);
        return uploadWithRetry(file, buffer, attempt + 1);
      }
      throw e;
    }
  };

  const handleLoadFile = async (file: File) => {
    setError('');

    if (!file.name.toLowerCase().endsWith(CSV_EXTENSION)) {
      setError('Only CSV files are supported. Please save the file as CSV and upload it again');
      return;
    }

    if (file.size > IMPORT_MAX_FILE_SIZE_MB * BYTES_IN_MB) {
      setError(`The file is too large. The maximum size is ${IMPORT_MAX_FILE_SIZE_MB} MB`);
      return;
    }

    setIsUploading(true);
    setUploadingFileName(file.name);
    setUploadProgress(0);

    try {
      const buffer = await file.arrayBuffer();
      const rowCount = countCsvDataRows(new TextDecoder().decode(buffer));

      if (rowCount > maxRows) {
        setError(
          `The file has ${rowCount} websites. Your account's website limit is ${maxRows}, so a file can hold at most ${maxRows} websites. Split it into several files, or contact support if you need a higher website limit`
        );
        return;
      }

      const chunks = await uploadWithRetry(file, buffer);
      const { uid } = chunks[chunks.length - 1];

      await launch(uid, file.name);
      setView('status');
    } catch (e) {
      setError(
        isTransportError(e)
          ? UPLOAD_INTERRUPTED_MESSAGE
          : parseApiErrorMessage(e) || 'The file could not be uploaded. Please try again'
      );
      // A rejected launch may mean another import started elsewhere (second tab, teammate);
      // pick it up so the status view is not stale.
      refetch();
    } finally {
      setIsUploading(false);
    }
  };

  // request() already toasts gRPC failures; only an empty FileResponse needs our own message.
  const notifyDownloadFailure = (title: string) => (e: unknown) => {
    if (!(e instanceof ApiError)) {
      dispatch(addNotification({ title, type: 'error' }));
    }
  };

  const handleDownloadTemplate = () => {
    setIsTemplateLoading(true);
    downloadImportTemplate()
      .catch(notifyDownloadFailure('The template could not be downloaded'))
      .finally(() => setIsTemplateLoading(false));
  };

  const handleDownloadReport = () => {
    setIsReportLoading(true);
    downloadImportReport(item.id, workspaceId)
      .catch(notifyDownloadFailure('The report could not be downloaded'))
      .finally(() => setIsReportLoading(false));
  };

  const handleCancelImport = () => {
    cancel()
      .then(() =>
        dispatch(addNotification({ title: 'Import canceled', type: 'success', delay: 3000 }))
      )
      .catch(() => {
        // request() already showed the error toast
      });
  };

  const isBusy = isUploading || isLaunching;

  const handleClose = () => {
    if (!isBusy) {
      onClose();
    }
  };

  const renderUploadView = () => (
    <>
      <div className="import-websites-modal__body">
        <p className="import-websites-modal__description">
          Download the template, fill in one website per row and upload it back. Websites are
          created as drafts with the pricing from the file, so you only need to verify each one
          afterwards. Up to <span data-testid="publisher-import-max-rows">{maxRows}</span> websites
          (your account&apos;s website limit) and {IMPORT_MAX_FILE_SIZE_MB} MB per file.
        </p>

        <Button
          type="additional"
          size="m"
          className="import-websites-modal__template-button"
          onClick={handleDownloadTemplate}
          isLoading={isTemplateLoading}
          data-testid="publisher-import-template-download"
          loadingText="Preparing template..."
        >
          <SVGIcon icon="download" />
          <span>Download CSV template</span>
        </Button>

        {isBusy ? (
          <div className="import-websites-modal__upload-progress">
            <ProgressBar
              title={isLaunching ? 'Checking the file...' : `Uploading ${uploadingFileName}`}
              // 0 + no counter renders ProgressBar's indeterminate (sliding) state while the
              // backend validates the file.
              percentage={isLaunching ? 0 : uploadProgress}
              withPercentsCounter={!isLaunching}
            />
          </div>
        ) : (
          <CsvDropZone onLoadFile={handleLoadFile} />
        )}

        {error ? (
          <div className="import-websites-modal__error" data-testid="publisher-import-error">
            <Alert variant="error">{error}</Alert>
          </div>
        ) : null}

        <ImportColumnsReference
          isOpen={isReferenceOpen}
          onToggle={() => setIsReferenceOpen((prev) => !prev)}
        />
      </div>

      <div className="import-websites-modal__footer">
        <Button
          type="secondary"
          onClick={onClose}
          disabled={isBusy}
          data-testid="publisher-import-close"
        >
          Close
        </Button>
      </div>
    </>
  );

  const renderStatusView = () => (
    <>
      <div className="import-websites-modal__body">
        <ImportStatusDetails item={item} />
      </div>

      <div className="import-websites-modal__footer">
        {isInProgress ? (
          <>
            <Button
              type="secondary"
              withConfirm
              onClick={handleCancelImport}
              isLoading={isCanceling}
              data-testid="publisher-import-cancel"
              loadingText="Canceling..."
            >
              Cancel import
            </Button>
            <Button onClick={onClose}>Close</Button>
          </>
        ) : (
          <>
            <Button
              type="secondary"
              onClick={handleDownloadReport}
              isLoading={isReportLoading}
              data-testid="publisher-import-report-download"
              loadingText="Preparing..."
            >
              Download report
            </Button>
            <Button onClick={() => setView('upload')} data-testid="publisher-import-another">
              Import another file
            </Button>
          </>
        )}
      </div>
    </>
  );

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={handleClose}
      closeButton={!isBusy}
      closeOnBackgroundClick={!isBusy}
      classNames={{ modal: 'import-websites-modal' }}
      testId="publisher-import-modal"
    >
      <div className="import-websites-modal__header">Import websites from CSV</div>
      {view === 'status' && item?.id > 0 ? renderStatusView() : renderUploadView()}
    </BaseModal>
  );
}

export default ImportWebsitesModal;
